import {z} from '@hono/zod-openapi';

import {isNil} from 'ramda';

import {COMPOSE_MESSAGE_BODY_MAX_LENGTH} from '../constants/COMPOSE_MESSAGE_BODY_MAX_LENGTH';
import {COMPOSE_MESSAGE_MAX_PLACEHOLDERS} from '../constants/COMPOSE_MESSAGE_MAX_PLACEHOLDERS';
import {composeMessageLocaleSchema} from './ComposeMessageLocaleSchema';
import {composeMessagePlaceholderSchema} from './ComposeMessagePlaceholderSchema';
import {composeMessageSourceSchema} from './ComposeMessageSourceSchema';

export const composeMessageRequestSchema = z
  .object({
    operation: z.enum(['generate', 'refine']),
    channel: z.enum(['email', 'sms', 'chat']),
    instruction: z.string().max(2000),
    body: z.string().max(COMPOSE_MESSAGE_BODY_MAX_LENGTH),
    subject: z.string().max(500).optional(),
    bodyFormat: z.enum(['HTML', 'PLAIN_TEXT']),
    refinement: z.enum(['improve', 'shorter', 'formal', 'detail', 'selection']).optional(),
    /**
     * The selected fragment of `body` a selection refinement rewrites. `body` then carries the
     * whole message, so the model sees what the fragment has to fit into.
     */
    selection: z.string().max(COMPOSE_MESSAGE_BODY_MAX_LENGTH).optional(),
    locale: composeMessageLocaleSchema.optional(),
    source: composeMessageSourceSchema.optional(),
    /** The variables a template may use. Grounds a template message the way a record grounds a record message. */
    placeholders: z
      .array(composeMessagePlaceholderSchema)
      .max(COMPOSE_MESSAGE_MAX_PLACEHOLDERS)
      .optional(),
  })
  .strict()
  .superRefine((request, context) => {
    const hasPlaceholderCatalogue = (request.placeholders?.length ?? 0) > 0;

    if (request.operation === 'generate') {
      if (request.channel !== 'email' && !hasPlaceholderCatalogue) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Only email supports generation without a template variable catalogue',
          path: ['channel'],
        });
      }
      if (isNil(request.source) && !hasPlaceholderCatalogue) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Generation requires a source record or a template variable catalogue',
          path: ['source'],
        });
      }
      if (request.instruction.trim() === '') {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Describe the requested email',
          path: ['instruction'],
        });
      }
      if (!isNil(request.refinement)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Generation cannot use a refinement action',
          path: ['refinement'],
        });
      }
    } else {
      if (request.body.trim() === '') {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Refinement requires existing content',
          path: ['body'],
        });
      }
      if (isNil(request.refinement)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Choose a refinement action',
          path: ['refinement'],
        });
      }
    }
    if (request.channel !== 'email' && !isNil(request.subject)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Only email supports a subject',
        path: ['subject'],
      });
    }
    if (request.channel !== 'email' && request.bodyFormat !== 'PLAIN_TEXT') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'SMS and chat require plain text',
        path: ['bodyFormat'],
      });
    }
    if (request.refinement === 'selection' && !isNil(request.subject)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Selection refinement cannot change the subject',
        path: ['subject'],
      });
    }
    if (!isNil(request.selection) && request.refinement !== 'selection') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Only a selection refinement carries a selected fragment',
        path: ['selection'],
      });
    }
    if (!isNil(request.selection) && request.selection.trim() === '') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The selected fragment is empty',
        path: ['selection'],
      });
    }
  })
  .openapi('ComposeMessageRequest');

export type ComposeMessageRequest = z.infer<typeof composeMessageRequestSchema>;
